import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { studentProfilePath } from './advisors';
import CommonHeader from '../../../util_components/common_header';
import UserCombobox from '../../../util_components/user_combobox/UserCombobox';
import * as actions from '../../../../redux/action_creators';
import { hasAccess } from '../../../../util_functions';
import AdvisorHistoryModal from './AdvisorHistoryModal';
import { useAdvisorAccess } from './useCanManageAdvisors';
import {
  SOURCE_NOT_ACTIVE,
  TARGET_INELIGIBLE,
  apiErrorCode,
  apiErrorReason,
  apiStatus,
  commitReassign,
  endAssignment,
  fetchAdvisors,
  previewReassign
} from './api';
import {
  ADVISORS_TAB_PATH,
  EMPTY_FILTERS,
  MAX_COMMIT_ITEMS,
  PREVIEW_CAP,
  REASSIGN_ACTION,
  SOURCES,
  advisorOptions,
  advisorPagePath,
  commitRequests,
  daysLeft,
  eligibleTargets,
  endNotice,
  endRequest,
  formatDay,
  formatTs,
  hasNarrowingFilters,
  listQuery,
  outcomeLabel,
  previewByAdvisor,
  previewByStudents,
  previewTruncated,
  runCommit,
  skipReasonLabel,
  sourceLabel,
  studentNames
} from './advisors';
import type { Filters } from './advisors';
import { SHARED_STYLES } from '../styles';
import { PLAIN_PILL, SUCCESS_PILL, WARN_PILL } from './pills';
import { Btn, LABEL_CLASS, SalesDialog } from '../ui';
import StackTd from '../StackTable';
import { stackClasses } from '../stack_table';
import type { Notify } from './AdvisorsPanel';
import type { AdvisorRow, AdvisorSummary, Commit, Preview, PreviewItem } from './types';

interface PreviewState {
  preview: Preview;
  to: string;
  byAdvisor: boolean;
}

const { FIELD } = SHARED_STYLES;

// Fixed widths (646px) leave Student ~137px at a 1024px viewport with the sidebar open.
const ROW_COLS: { label: string; width?: number }[] = [
  { label: '', width: 36 },
  { label: 'Student' },
  { label: 'Source', width: 140 },
  { label: 'Assigned', width: 104 },
  { label: 'Expires', width: 110 },
  { label: 'Assigned by', width: 120 },
  { label: '', width: 136 }
];
const CELL = 'px-3.5 py-2.5 text-left text-[13px] font-normal';
const ST = stackClasses();
const LABEL = 'text-xs text-content-muted';
const ROW = `border-t border-line-subtle text-[12.5px] hover:bg-surface-sunken ${ST.row}`;
const RESULT_COLLAPSED = 5;

const dash = (v: string | null | undefined) => v || '—';
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

interface Props {
  advisorUuid: string;
  notify: Notify;
  // can_write_advisors (/me); without it the page is view-only: filters and History, no writes.
  canWrite: boolean;
}

// One advisor's active students: filter, reassign, end, history.
export function AdvisorStudents({ advisorUuid, notify, canWrite }: Props) {
  const router = useHistory();
  const base: Filters = { ...EMPTY_FILTERS, advisor: advisorUuid };
  const [draft, setDraft] = useState<Filters>(base);
  const [applied, setApplied] = useState<Filters>(base);
  const [summary, setSummary] = useState<AdvisorSummary[]>([]);
  const [rows, setRows] = useState<AdvisorRow[]>([]);
  const [nextCursor, setNextCursor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<{ forbidden: boolean; reason: string } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [result, setResult] = useState<Commit | null>(null);
  const [resultExpanded, setResultExpanded] = useState(false);
  // Captured at commit time: the reload drops moved students from the filtered rows.
  const [resultItems, setResultItems] = useState<PreviewItem[]>([]);
  const [historyStudent, setHistoryStudent] = useState<{ uuid: string; name: string } | null>(null);
  const [endTarget, setEndTarget] = useState<AdvisorRow | null>(null);
  const [ending, setEnding] = useState(false);
  // Drops responses from a superseded load (filter changed mid-flight).
  const loadSeq = useRef(0);

  const load = useCallback(
    (filters: Filters) => {
      loadSeq.current += 1;
      const seq = loadSeq.current;
      setLoading(true);
      return fetchAdvisors(listQuery(filters))
        .then((d) => {
          if (seq !== loadSeq.current) return;
          setSummary(d.summary);
          setRows(d.rows);
          setNextCursor(d.next_cursor);
          setLoadError(null);
        })
        .catch((e: unknown) => {
          if (seq !== loadSeq.current) return;
          const forbidden = apiStatus(e) === 403;
          const reason = forbidden
            ? 'Super user or sales manager only'
            : apiErrorReason(e, 'Failed to load advisors');
          // Never leave the previous filter's rows (and a live "Reassign all") under new inputs.
          setSummary([]);
          setRows([]);
          setNextCursor(0);
          // The target box goes blank with the summary, so drop the hidden target and open preview too.
          setTarget('');
          setPreviewState(null);
          setLoadError({ forbidden, reason });
          if (!forbidden) notify('error', reason);
        })
        .finally(() => {
          if (seq === loadSeq.current) setLoading(false);
        });
    },
    [notify]
  );

  useEffect(() => {
    load(applied);
  }, [load, applied]);

  const apply = (next: Filters) => {
    setDraft(next);
    setApplied(next);
    setSelected(new Set());
    setPreviewState(null);
    setResult(null);
  };

  const resetFilters = () => apply({ ...EMPTY_FILTERS, advisor: advisorUuid });

  const reload = () => {
    setSelected(new Set());
    return load(applied);
  };

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    const seq = loadSeq.current;
    setLoadingMore(true);
    fetchAdvisors(listQuery(applied, nextCursor))
      .then((d) => {
        if (seq !== loadSeq.current) return;
        setRows((prev) => [...prev, ...d.rows]);
        setNextCursor(d.next_cursor);
      })
      .catch((e: unknown) => notify('error', apiErrorReason(e, 'Failed to load more students')))
      .finally(() => setLoadingMore(false));
  };

  const toggle = (uuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.student_uuid));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.student_uuid)));

  const advisor = summary.find((s) => s.cms_user_uuid === advisorUuid);
  const targets = useMemo(
    () => eligibleTargets(summary).filter((t) => t.uuid !== advisorUuid),
    [summary, advisorUuid]
  );
  const advisors = useMemo(() => advisorOptions(summary), [summary]);
  const unknownAdvisor = !loading && !loadError && !advisor;
  const advisorName = advisor?.name || '';

  const switchAdvisor = (uuid: string) => {
    if (uuid && uuid !== advisorUuid) router.push(advisorPagePath(uuid));
  };

  const startPreview = (body: ReturnType<typeof previewByStudents>) => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    previewReassign(body)
      .then((preview) =>
        setPreviewState({ preview, to: target, byAdvisor: 'from_cms_user_uuid' in body })
      )
      .catch((e: unknown) => {
        const reason = apiErrorReason(e, 'Failed to preview reassignment');
        notify(
          'error',
          apiErrorCode(e) === TARGET_INELIGIBLE
            ? `Target advisor is not eligible: ${reason}`
            : reason
        );
      })
      .finally(() => setBusy(false));
  };

  const onPreviewSelected = () => startPreview(previewByStudents(Array.from(selected), target));
  const onPreviewAll = () => startPreview(previewByAdvisor(advisorUuid, target));

  const onConfirm = () => {
    if (!previewState || busy) return;
    const { preview, to } = previewState;
    const requests = commitRequests(preview, to);
    if (!requests.length) return;
    setBusy(true);
    runCommit(preview.batch_ref, requests, commitReassign)
      .then(({ commit, error }) => {
        setResultItems(preview.items);
        setResult(commit);
        setResultExpanded(false);
        setPreviewState(null);
        // Any open history may now be stale.
        setHistoryStudent(null);
        if (error) {
          notify(
            'error',
            `${apiErrorReason(error, 'Commit failed')}. ${commit.counts.reassigned} reassigned before the error; retry is safe.`
          );
        } else {
          notify('success', `Reassigned ${commit.counts.reassigned} student(s)`);
        }
        return reload();
      })
      .finally(() => setBusy(false));
  };

  // Reload on success and on SOURCE_NOT_ACTIVE; a generic error keeps the modal and list.
  const onEndConfirm = () => {
    if (!endTarget || ending) return;
    const row = endTarget;
    const closeHistory = () =>
      setHistoryStudent((h) => (h && h.uuid === row.student_uuid ? null : h));
    setEnding(true);
    endAssignment(endRequest(row))
      .then((res) => {
        notify('success', endNotice(row, res));
        setEndTarget(null);
        closeHistory();
        return reload();
      })
      .catch((e: unknown) => {
        if (apiErrorCode(e) === SOURCE_NOT_ACTIVE) {
          notify('error', 'This assignment already changed');
          setEndTarget(null);
          closeHistory();
          return reload();
        }
        notify('error', apiErrorReason(e, 'Failed to end the assignment'));
        return null;
      })
      .finally(() => setEnding(false));
  };

  const headerName = () => {
    if (advisor) return advisor.name;
    if (loading) return 'Loading…';
    return unknownAdvisor ? 'Unknown advisor' : 'Advisor';
  };

  const renderHeader = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-subtle px-3.5 py-3.5">
      <div className="min-w-0">
        <Link
          to={ADVISORS_TAB_PATH}
          className="text-xs font-semibold text-content-link hover:underline"
        >
          ← All advisors
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h2 className="m-0 text-lg font-semibold">{headerName()}</h2>
          {advisor ? (
            <>
              {advisor.level ? <span className={PLAIN_PILL}>{advisor.level}</span> : null}
              <span className={advisor.roster_status === 'LEFT' ? WARN_PILL : SUCCESS_PILL}>
                {advisor.roster_status === 'LEFT' ? 'Left the roster' : 'Active roster'}
              </span>
              <span className={advisor.has_sales ? PLAIN_PILL : WARN_PILL}>
                {advisor.has_sales ? 'SALES role' : 'No SALES role'}
              </span>
              <span className="text-xs text-content-muted">
                {plural(advisor.active_students, 'active student')}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <UserCombobox
          id="advisor-switch"
          label="Switch advisor"
          placeholder="Switch advisor…"
          users={advisors}
          value={advisorUuid}
          on_change={switchAdvisor}
          width={220}
        />
        <span className="text-xs text-content-muted">Times in IST</span>
      </div>
    </div>
  );

  const renderFilters = () => (
    <form
      className="flex flex-row flex-wrap items-end justify-start gap-2.5 border-b border-line-subtle px-3.5 py-3"
      onSubmit={(e) => {
        e.preventDefault();
        apply(draft);
      }}
    >
      <label htmlFor="advisors-filter-source" className={LABEL}>
        Source
        <select
          id="advisors-filter-source"
          value={draft.source}
          onChange={(e) => apply({ ...draft, source: e.target.value as Filters['source'] })}
          className="block text-sm"
          style={FIELD}
        >
          <option value="">Any</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {sourceLabel(s)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor="advisors-filter-days" className={LABEL}>
        Expiring within (days)
        <input
          id="advisors-filter-days"
          type="number"
          min={1}
          value={draft.expiringWithinDays}
          onChange={(e) => setDraft({ ...draft, expiringWithinDays: e.target.value })}
          className="block text-sm"
          style={{ ...FIELD, width: 96 }}
        />
      </label>
      <label htmlFor="advisors-filter-q" className={LABEL}>
        Student
        <input
          id="advisors-filter-q"
          type="text"
          placeholder="Name or email"
          value={draft.q}
          onChange={(e) => setDraft({ ...draft, q: e.target.value })}
          className="block text-sm"
          style={{ ...FIELD, width: 220 }}
        />
      </label>
      {/* Neutral: the panel's one orange button is Preview reassign. */}
      <Btn type="submit" variant="neutral">
        Search
      </Btn>
      <Btn variant="ghost" onClick={resetFilters}>
        Reset
      </Btn>
    </form>
  );

  // Read-only callers lose the select column; its header cell is the select-all box.
  const cols = canWrite ? ROW_COLS : ROW_COLS.slice(1);

  const renderActions = () =>
    canWrite ? (
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2.5 border-b border-line-subtle bg-surface-card px-3.5 py-2.5">
        <span className="text-[13px] font-semibold tabular-nums">{`${selected.size} selected`}</span>
        <UserCombobox
          id="advisors-target"
          label="Reassign to"
          placeholder="Reassign to…"
          users={targets}
          value={target}
          on_change={setTarget}
          disabled={busy}
          width={240}
        />
        <Btn
          variant="primary"
          onClick={onPreviewSelected}
          disabled={!target || selected.size === 0 || selected.size > MAX_COMMIT_ITEMS || busy}
        >
          Preview reassign
        </Btn>
        <Btn
          variant="neutral"
          onClick={onPreviewAll}
          disabled={!target || busy || !advisor || advisor.active_students === 0}
        >
          {advisor ? `Reassign all (${advisor.active_students})` : 'Reassign all'}
        </Btn>
        {selected.size > MAX_COMMIT_ITEMS ? (
          <span className="text-xs text-content-muted">
            {`Select at most ${MAX_COMMIT_ITEMS} students, or reassign all of them.`}
          </span>
        ) : null}
      </div>
    ) : (
      <div className="border-b border-line-subtle px-3.5 py-2.5 text-xs text-content-muted">
        View only: only sales managers can assign or reassign.
      </div>
    );

  const renderPreviewModal = () => {
    const batches = previewState ? commitRequests(previewState.preview, previewState.to).length : 0;
    return (
      <SalesDialog
        open={!!previewState}
        // Escape and the overlay are ignored while a commit is running.
        onClose={() => (busy ? undefined : setPreviewState(null))}
        title="Preview reassign"
        width={860}
      >
        {previewState ? (
          // Buttons stay inside the region so the preview reads (and tests) as one unit.
          <div role="region" aria-label="Reassignment preview">
            <div className="px-4 py-3">
              <h3 className="m-0 text-[13px] font-semibold">
                {`Reassign to ${previewState.preview.to.name}${previewState.preview.to.level ? ` (${previewState.preview.to.level})` : ''}`}
              </h3>
              <p className="m-0 mt-1 text-xs text-content-muted">
                {`${previewState.preview.counts.reassign} to reassign, ${previewState.preview.counts.skip} skipped.`}
                {batches > 1 ? ` Sent in ${batches} batches.` : ''}
              </p>
              {previewTruncated(previewState.preview, previewState.byAdvisor) ? (
                <p className="m-0 mt-1 text-xs text-content-muted">
                  {`Only the first ${PREVIEW_CAP} are shown; preview again after confirming to move the rest.`}
                </p>
              ) : null}
              <div
                className="mt-2 overflow-auto rounded-[10px] border border-line-subtle"
                style={{ maxHeight: '50vh' }}
              >
                <table className={ST.table} aria-label="Preview items">
                  <thead className={`border-b border-line-subtle bg-surface-sunken ${ST.head}`}>
                    <tr>
                      {['Student', 'From', 'Expires', 'Action'].map((c) => (
                        <th key={c} className={`px-3 py-2 text-left ${LABEL_CLASS}`}>
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={ST.body}>
                    {previewState.preview.items.map((i) => (
                      <tr
                        key={`${i.student_uuid}-${i.row_id}`}
                        className={`border-t border-line-subtle text-[12.5px] ${ST.row}`}
                      >
                        <StackTd
                          className="truncate px-3 py-2 text-left font-semibold"
                          title={i.student_name}
                        >
                          {i.student_name}
                        </StackTd>
                        <StackTd label="From" className="px-3 py-2 text-left truncate">
                          {dash(i.from_name)}
                        </StackTd>
                        <StackTd label="Expires" className="px-3 py-2 text-left">
                          {formatTs(i.expires_at)}
                        </StackTd>
                        <StackTd label="Action" className="px-3 py-2 text-left">
                          {i.action === REASSIGN_ACTION
                            ? 'Reassign'
                            : `Skip: ${skipReasonLabel(i.skip_reason) || i.action}`}
                        </StackTd>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t border-line-subtle bg-surface-sunken px-4 py-3">
              <Btn variant="neutral" onClick={() => setPreviewState(null)} disabled={busy}>
                Cancel
              </Btn>
              <Btn
                variant="primary"
                onClick={onConfirm}
                disabled={busy || previewState.preview.counts.reassign === 0}
              >
                {busy
                  ? 'Reassigning…'
                  : `Confirm reassign (${previewState.preview.counts.reassign})`}
              </Btn>
            </div>
          </div>
        ) : null}
      </SalesDialog>
    );
  };

  const renderResult = () => {
    if (!result) return null;
    const names = studentNames(resultItems, rows);
    const shown = resultExpanded ? result.results : result.results.slice(0, RESULT_COLLAPSED);
    return (
      <div
        className="mx-3.5 my-3 rounded-[10px] border border-line-subtle bg-surface-sunken p-3 text-[13px]"
        role="region"
        aria-label="Reassignment result"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-[13px] font-semibold">Reassignment result</h3>
            <p className="m-0 mt-1 text-xs text-content-muted">
              {`${result.counts.reassigned} reassigned, ${result.counts.duplicate} already done, ${result.counts.skipped} skipped.`}
            </p>
          </div>
          <Btn variant="neutral" size="row" onClick={() => setResult(null)}>
            Dismiss
          </Btn>
        </div>
        <ul className="m-0 mt-2 pl-5 text-[12.5px]">
          {shown.map((r) => (
            <li key={`${r.student_uuid}-${r.row_id}`}>
              {`${names.get(r.student_uuid) || r.student_uuid}: ${outcomeLabel(r.outcome)}`}
              {r.reason ? ` (${skipReasonLabel(r.reason)})` : ''}
            </li>
          ))}
        </ul>
        {result.results.length > RESULT_COLLAPSED ? (
          <button
            type="button"
            onClick={() => setResultExpanded((v) => !v)}
            className="mt-2 text-xs font-semibold hover:underline"
            style={{
              background: 'transparent',
              border: 'none',
              margin: 0,
              padding: 0,
              color: 'var(--text-link-brand)'
            }}
          >
            {resultExpanded ? 'Show fewer' : `Show all ${result.results.length}`}
          </button>
        ) : null}
      </div>
    );
  };

  const renderExpires = (iso: string) => {
    const d = daysLeft(iso, Date.now());
    return (
      <StackTd label="Expires" className={`${CELL} whitespace-nowrap`} title={formatTs(iso)}>
        <div>{formatDay(iso)}</div>
        {d !== null && d <= 0 ? <span className={WARN_PILL}>Expired</span> : null}
        {d !== null && d > 0 ? (
          <div className="text-[11.5px] text-content-muted">{`in ${d}d`}</div>
        ) : null}
      </StackTd>
    );
  };

  const renderRow = (r: AdvisorRow) => (
    <tr key={r.row_id} className={ROW}>
      {canWrite ? (
        <StackTd className={CELL}>
          <input
            type="checkbox"
            aria-label={`Select ${r.student_name}`}
            checked={selected.has(r.student_uuid)}
            onChange={() => toggle(r.student_uuid)}
          />
        </StackTd>
      ) : null}
      <StackTd className={`${CELL} truncate`} title={r.student_name}>
        <a
          href={studentProfilePath(r.student_uuid)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-content-link hover:underline"
        >
          {r.student_name || r.student_uuid}
        </a>
      </StackTd>
      <StackTd label="Source" className={`${CELL} whitespace-nowrap`}>
        <span
          className={`${PLAIN_PILL} max-w-full truncate align-middle`}
          title={sourceLabel(r.source)}
        >
          {sourceLabel(r.source)}
        </span>
      </StackTd>
      <StackTd
        label="Assigned"
        className={`${CELL} whitespace-nowrap`}
        title={formatTs(r.assigned_at)}
      >
        {formatDay(r.assigned_at)}
      </StackTd>
      {renderExpires(r.expires_at)}
      <StackTd
        label="Assigned by"
        className={`${CELL} truncate`}
        title={r.assigned_by_name || undefined}
      >
        {dash(r.assigned_by_name)}
      </StackTd>
      <StackTd className="px-3.5 py-2.5">
        <div className="flex justify-end gap-2 whitespace-nowrap max-md:justify-start">
          <Btn
            variant="neutral"
            size="row"
            onClick={() => setHistoryStudent({ uuid: r.student_uuid, name: r.student_name })}
            aria-label={`History of ${r.student_name}`}
          >
            History
          </Btn>
          {canWrite ? (
            <Btn
              variant="danger"
              size="row"
              onClick={() => setEndTarget(r)}
              aria-label={`End assignment of ${r.student_name}`}
            >
              End
            </Btn>
          ) : null}
        </div>
      </StackTd>
    </tr>
  );

  const renderEmptyRow = () => {
    let body: React.ReactNode;
    if (loading) body = 'Loading students…';
    else if (hasNarrowingFilters(applied)) {
      body = (
        <>
          No students match these filters.{' '}
          <button
            type="button"
            onClick={resetFilters}
            className="font-semibold hover:underline"
            style={{
              background: 'transparent',
              border: 'none',
              margin: 0,
              padding: 0,
              color: 'var(--text-link-brand)'
            }}
          >
            Clear filters
          </button>
        </>
      );
    } else body = `${advisorName || 'This advisor'} has no active students.`;
    return (
      <tr className={ST.row}>
        <StackTd colSpan={cols.length} className="px-4 py-8 text-center text-content-muted">
          {body}
        </StackTd>
      </tr>
    );
  };

  const renderRows = () => {
    if (loadError && rows.length === 0) {
      return (
        <div className="px-4 py-8 text-center">
          <div role="alert" className="text-[13px]" style={{ color: 'var(--state-error)' }}>
            {loadError.forbidden
              ? loadError.reason
              : `Could not load advisors: ${loadError.reason}`}
          </div>
          {loadError.forbidden ? null : (
            <Btn
              variant="outline"
              size="row"
              onClick={reload}
              disabled={loading}
              style={{ marginTop: 12 }}
            >
              {loading ? 'Retrying…' : 'Retry'}
            </Btn>
          )}
        </div>
      );
    }
    return (
      <>
        <table className={ST.table} aria-label="Active advisor assignments">
          <colgroup>
            {cols.map((c, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <col key={i} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
          {/* Card mode keeps only the select-all checkbox of the header (none when read-only). */}
          <thead
            className={`border-b border-line-subtle bg-surface-sunken ${canWrite ? 'max-md:block' : 'max-md:hidden'}`}
          >
            <tr className="max-md:block">
              {cols.map((c, i) =>
                canWrite && i === 0 ? (
                  <th key="select" className={`${CELL} max-md:block`}>
                    <input
                      type="checkbox"
                      aria-label="Select all loaded"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={rows.length === 0}
                    />
                  </th>
                ) : (
                  // eslint-disable-next-line react/no-array-index-key
                  <th key={`${c.label}-${i}`} className={`${CELL} ${LABEL_CLASS} max-md:hidden`}>
                    {c.label}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className={ST.body}>
            {rows.length === 0 ? renderEmptyRow() : rows.map(renderRow)}
          </tbody>
        </table>
        {nextCursor > 0 ? (
          <div className="border-t border-line-subtle px-3.5 py-3">
            <Btn variant="neutral" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </Btn>
          </div>
        ) : null}
      </>
    );
  };

  const renderEndModal = () => (
    <SalesDialog
      open={!!endTarget}
      onClose={() => (ending ? undefined : setEndTarget(null))}
      title="End assignment"
      width={480}
    >
      {endTarget ? (
        <div role="alertdialog" aria-label="Confirm end assignment">
          <p className="m-0 px-4 py-3.5 text-[13px]">
            {`End ${endTarget.student_name}'s assignment with ${advisorName || endTarget.advisor_name}? Attribution stops now.`}
          </p>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line-subtle bg-surface-sunken px-4 py-3">
            <Btn variant="neutral" onClick={() => setEndTarget(null)} disabled={ending}>
              Cancel
            </Btn>
            <Btn variant="primary" onClick={onEndConfirm} disabled={ending}>
              {ending ? 'Ending…' : 'Yes, end assignment'}
            </Btn>
          </div>
        </div>
      ) : null}
    </SalesDialog>
  );

  return (
    <section className="mx-4 my-5 rounded-[10px] border border-line-subtle bg-surface-card text-[13px] text-content-primary">
      {renderHeader()}
      {unknownAdvisor ? (
        <div
          role="alert"
          className="mx-3.5 my-4 rounded-lg bg-state-warning-bg p-3 text-[13px] text-state-warning-text"
        >
          This advisor has no active students and is not on the sales roster. Pick another one from
          Switch advisor, or go back to All advisors.
        </div>
      ) : (
        <>
          {renderFilters()}
          {renderActions()}
          {renderResult()}
          {renderRows()}
        </>
      )}
      {canWrite ? renderPreviewModal() : null}
      <AdvisorHistoryModal student={historyStudent} onClose={() => setHistoryStudent(null)} />
      {canWrite ? renderEndModal() : null}
    </section>
  );
}

// Route: /sales-task-list/advisors/:advisorUuid (opened in a new tab from the Advisors tab).
function AdvisorStudentsPage() {
  const { advisorUuid } = useParams<{ advisorUuid: string }>();
  const accessType = useSelector(
    (state: { home: { access_type?: string } }) => state.home.access_type
  );
  const { access, canWrite } = useAdvisorAccess(hasAccess(accessType || '', 'SUPER_USER'));
  const dispatch = useDispatch();
  const notify = useCallback<Notify>(
    (type, message) => dispatch(actions.set_notification_variable(true, type, message)),
    [dispatch]
  );

  let body: React.ReactNode;
  if (access === 'allowed') {
    // key: switching advisors remounts, so filters, selection and results never carry over.
    body = (
      <AdvisorStudents
        key={advisorUuid}
        advisorUuid={advisorUuid}
        notify={notify}
        canWrite={canWrite}
      />
    );
  } else {
    body = (
      <section className="mx-4 my-5 rounded-[10px] border border-line-subtle bg-surface-card px-5 py-6 text-[13px]">
        <span role={access === 'denied' ? 'alert' : undefined} className="text-content-muted">
          {access === 'denied' ? 'Super user or sales manager only' : 'Loading…'}
        </span>
      </section>
    );
  }
  return (
    <div>
      <CommonHeader title="Advisor students" />
      {body}
    </div>
  );
}

export default AdvisorStudentsPage;
